"use client";

import React, { useState, FormEvent } from "react";
import { PatternFormat } from "react-number-format";
import { FaTimes } from "react-icons/fa";
import { actualSite, BRAND_NAMES, formatName } from "@/app/utils/utils";
import { sendToSyonet } from "@/app/services/syonetIntegration";
import { normalizeModelName } from "@/app/services/handleSubmit";
import { pushDataLayerEvent } from "@/app/utils/dataLayer";
import type { FormPayload } from "@/app/interface/components";

const PAGE_VALUE = "LP-YARIS-CROSS-360";
const AVAILABLE_CARS = ["YARIS CROSS"];

interface WhatsAppFormModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const WhatsAppFormModal: React.FC<WhatsAppFormModalProps> = ({ isOpen, onClose }) => {
	const { lojas } = actualSite();
	const [formData, setFormData] = useState({
		name: "",
		phone: "",
		email: "",
		cpf: "",
		model: "YARIS CROSS",
		dealer: "",
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formSubmitted, setFormSubmitted] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	const inputClassName =
		"w-full p-3 border-b-2 border-gray-400 focus:outline-none focus:ring-2 focus:ring-grey-600 focus:border-transparent transition-all";
	const inputSelectClassName =
		"w-full p-2 rounded border-2 border-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all";

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setFormError(null);
		setIsSubmitting(true);

		try {
			const name = (formData.name || "").trim();
			const email = (formData.email || "").trim();
			const phone = (formData.phone || "").replace(/\D/g, "");
			const cpf = (formData.cpf || "").replace(/\D/g, "");
			const model = (formData.model || "").trim();
			const dealerRaw = (formData.dealer || "").trim();
			const dealer = formatName(dealerRaw);

			const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ]+ [A-Za-zÀ-ÖØ-öø-ÿ ]+$/;
			if (!nameRegex.test(name)) {
				setFormError("Por favor, digite seu nome completo (nome e sobrenome).");
				return;
			}
			if (phone.length !== 11) {
				setFormError("Telefone inválido. Informe DDD + número (11 dígitos).");
				return;
			}
			if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
				setFormError("Digite um email válido.");
				return;
			}
			if (cpf.length !== 11) {
				setFormError("CPF inválido. Informe 11 dígitos.");
				return;
			}
			if (!model) {
				setFormError("Selecione o modelo.");
				return;
			}
			if (!dealerRaw) {
				setFormError("Selecione a unidade.");
				return;
			}

			const urlParams = new URLSearchParams(window.location.search);
			const utmParams = urlParams.toString();

			const requestBody = {
				name,
				email,
				cpf,
				phone,
				params: utmParams,
				consorcio: false,
				registrationNumber: cpf,
				team: BRAND_NAMES.ALL_CAPS,
				locationId: 1,
				model: model || "YARIS CROSS",
				page: PAGE_VALUE,
				wpp: true,
				chatbot: PAGE_VALUE,
				categoryId: 1,
				brandId: 67260,
				dealer: dealer || "Whatsapp",
				contactMethod: "WHATSAPP",
				categoryTitle: "Veiculos Novos",
				productTitle: "YARIS CROSS",
			};

			const response = await fetch("/api/submitForm", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify(requestBody),
			});

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`Erro ao enviar formulário: ${errorText}`);
			}

			pushDataLayerEvent("lead_whatsapp");

			try {
				const syonetPayload: FormPayload & { categoria?: string } = {
					name,
					email,
					cellPhone: phone,
					registrationNumber: cpf,
					consorcio: false,
					team: BRAND_NAMES.ALL_CAPS,
					locationId: 1,
					model: normalizeModelName(model || "YARIS CROSS"),
					page: PAGE_VALUE,
					wpp: true,
					dealer: dealer || "Whatsapp",
					comment: `Chatbot - ${PAGE_VALUE}`,
					categoria: "0 Km",
				};
				await sendToSyonet(syonetPayload, "toyota", {
					skipToyotaLeadCenter: true,
				}).catch(() => {});
			} catch {
				// não bloqueia o fluxo
			}

			setFormSubmitted(true);
		} catch (error) {
			setFormError(
				error instanceof Error
					? error.message
					: "❌ Ocorreu um erro ao processar sua solicitação."
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50"
			onClick={onClose}
			role="dialog"
			aria-modal="true"
			aria-labelledby="whatsapp-form-title"
		>
			<main
				className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					type="button"
					onClick={onClose}
					className="absolute top-3 right-3 p-1 text-gray-500 hover:text-gray-700 rounded"
					aria-label="Fechar"
				>
					<FaTimes className="w-5 h-5" />
				</button>
				<div className="px-5 py-4">
					<h2 id="whatsapp-form-title" className="text-base text-black pr-8">
						Atendimento via WhatsApp
					</h2>
					<div className="h-[2px] w-1/4 bg-red-500 mb-2" />

					{formError && (
						<div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
							{formError}
						</div>
					)}

					{formSubmitted ? (
						<div className="p-4 rounded border border-green-200 bg-green-50 text-green-700 text-sm">
							✅ Dados enviados com sucesso! Abrindo WhatsApp...
						</div>
					) : (
						<form onSubmit={handleSubmit} className="flex flex-col gap-3 text-black">
							<div>
								<input
									className={inputClassName}
									value={formData.name}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, name: e.target.value }))
									}
									placeholder="Nome*"
								/>
							</div>
							<div>
								<input
									type="email"
									className={inputClassName}
									value={formData.email}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, email: e.target.value }))
									}
									placeholder="E-mail*"
								/>
							</div>
							<div>
								<PatternFormat
									value={formData.phone}
									onValueChange={({ value }) =>
										setFormData((prev) => ({ ...prev, phone: value }))
									}
									format="(##) #####-####"
									mask="_"
									className={inputClassName}
									placeholder="Telefone*"
								/>
							</div>
							<div>
								<PatternFormat
									value={formData.cpf}
									onValueChange={({ value }) =>
										setFormData((prev) => ({ ...prev, cpf: value }))
									}
									format="###.###.###-##"
									mask="_"
									className={inputClassName}
									placeholder="CPF*"
								/>
							</div>
							<div>
								<select
									className={inputSelectClassName}
									value={formData.model || ""}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, model: e.target.value }))
									}
								>
									<option value="">Selecione o modelo</option>
									{AVAILABLE_CARS.map((car) => (
										<option key={car} value={car}>
											{car}
										</option>
									))}
								</select>
							</div>
							<div>
								<select
									className={inputSelectClassName}
									value={formData.dealer || ""}
									onChange={(e) =>
										setFormData((prev) => ({
											...prev,
											dealer: e.target.value,
										}))
									}
								>
									<option value="">Selecione a unidade</option>
									{lojas.map((loja) => (
										<option key={loja.nome} value={loja.nome}>
											{formatName(loja.nome)}
										</option>
									))}
								</select>
							</div>
							<button
								type="submit"
								disabled={isSubmitting}
								className={`mt-2 px-4 py-2 rounded w-full transition-colors ${
									isSubmitting
										? "bg-gray-400 cursor-not-allowed"
										: "bg-red-600 hover:bg-red-700"
								} text-white font-semibold`}
							>
								{isSubmitting ? "Enviando..." : "Enviar e ir para WhatsApp"}
							</button>
						</form>
					)}
				</div>
			</main>
		</div>
	);
};

export default React.memo(WhatsAppFormModal);
